// Reusable editor for an ordered, toggleable list (Oracle widgets, bottom-nav
// tool slots). Each row has up/down arrows to set position and a switch to
// show/hide it — the "dropdown of which to show + up/down to order" pattern.
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './ThemedSwitch';
import { makeThemedStyles } from '../../v10/theme';
import { useOrderedConfig } from '../dashboardConfig';

export function OrderedConfigEditor({
  userId,
  column,
  catalog,
  defaults,
  label,
  renderIcon,
  hint,
  maxVisible,
}: {
  userId: string | undefined;
  column: string;
  catalog: string[];
  defaults: string[];
  label: (id: string) => string;
  renderIcon?: (id: string) => React.ReactNode;
  hint?: string;
  /** When set, disallow enabling more than this many rows (bottom nav slots). */
  maxVisible?: number;
}) {
  const styles = useStyles();
  const { items, visibleIds, setVisible, move, loading } = useOrderedConfig(userId, column, catalog, defaults);

  if (loading) return <Text style={styles.hint}>Loading…</Text>;

  const atCap = maxVisible != null && visibleIds.length >= maxVisible;

  return (
    <View>
      {hint ? <Text style={styles.hint}>{hint}</Text> : null}
      {maxVisible != null ? (
        <Text style={styles.hint}>
          {visibleIds.length} / {maxVisible} slots used.
        </Text>
      ) : null}
      {items.map((item, idx) => {
        const disableOn = !item.visible && atCap;
        return (
          <View key={item.id} style={styles.row}>
            <View style={styles.reorder}>
              <Pressable
                onPress={() => move(item.id, -1)}
                disabled={idx === 0}
                style={[styles.reorderBtn, idx === 0 && styles.reorderBtnDisabled]}
              >
                <Text style={styles.reorderText}>▲</Text>
              </Pressable>
              <Pressable
                onPress={() => move(item.id, 1)}
                disabled={idx === items.length - 1}
                style={[styles.reorderBtn, idx === items.length - 1 && styles.reorderBtnDisabled]}
              >
                <Text style={styles.reorderText}>▼</Text>
              </Pressable>
            </View>
            {renderIcon ? <View style={styles.icon}>{renderIcon(item.id)}</View> : null}
            <Text style={styles.label}>{label(item.id)}</Text>
            <ThemedSwitch
              value={item.visible}
              onValueChange={(v) => setVisible(item.id, v)}
              disabled={disableOn}
            />
          </View>
        );
      })}
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginTop: 6,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  reorder: { gap: 2 },
  reorderBtn: {
    width: 26,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  reorderBtnDisabled: { opacity: 0.25 },
  reorderText: { fontSize: 10, color: t.goldBright, fontWeight: '900' },
  icon: { width: 24, alignItems: 'center' },
  label: { flex: 1, fontSize: 14, fontWeight: '700', color: t.ivory },
}),
);
